/*
 * Getting started, step 3 (apps table): pure model, no React and nothing that touches `window`.
 */
import type { App } from '@hangar/shared';

type Linkable = Pick<App, 'easProjectId' | 'bitriseAppSlug'>;

/** The build setup in a client's words: ready once Hangar linked the app to its build service, else being prepared. */
export const buildSetupText = (app: Partial<Linkable>): string => (app.easProjectId || app.bitriseAppSlug ? 'Ready' : 'Being prepared');

/**
 * Where the app builds, shortened to fit a cell: its EAS project or its Bitrise app (native apps) for platform
 * operators; clients read the build setup's state (SPEC decision 23).
 */
export function buildsOnLabel(app: Linkable, seesProviders = false): string {
  if (!seesProviders) return buildSetupText(app);
  if (app.easProjectId) return `EAS ${app.easProjectId.slice(0, 8)}…`;
  if (!app.bitriseAppSlug) return 'not linked';
  const slug = app.bitriseAppSlug;
  return `Bitrise ${slug.slice(0, 10)}${slug.length > 10 ? '…' : ''}`;
}

/** The full id behind the shortened label, when there is one (operators only). */
export const buildsOnId = (app: Linkable, seesProviders = false): string | undefined => (seesProviders ? (app.easProjectId ?? app.bitriseAppSlug ?? undefined) : undefined);

/** Hover text of the runtime cell's bundle id. */
export const bundleIdTitle = (bundleId: string | undefined) => (bundleId ? 'Bundle id its profiles check' : 'No profile checks a bundle id yet');

export const BUILDS_ON_LINKED_CLASS = 'font-mono text-xs text-muted-foreground';
export const BUILDS_ON_UNLINKED_CLASS = 'font-mono text-xs text-subtle';

/** Muted text when linked, subtle when not. */
export const buildsOnClass = (app: Linkable) => (app.easProjectId || app.bitriseAppSlug ? BUILDS_ON_LINKED_CLASS : BUILDS_ON_UNLINKED_CLASS);

/** Title of the "Builds on" cell: the id, or what is missing (operators); the build setup's state for clients. */
export const buildsOnTitle = (app: Linkable, seesProviders = false) =>
  seesProviders ? (buildsOnId(app, true) ?? 'No EAS project or Bitrise app linked') : app.easProjectId || app.bitriseAppSlug ? 'Hangar has set up the builds of this app' : 'Hangar is preparing the builds of this app';

/** The header of the "Builds on" column: the provider link for operators, the build setup for clients. */
export const buildsOnHeader = (seesProviders: boolean): string => (seesProviders ? 'Builds on' : 'Build setup');
